import { Translate, useTranslation } from "@privacy/translations";
import type { TranslationKey } from "@privacy/translations";
import { useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { ArchiveIcon, EditIcon, InformationTypeIcon2, RotateCcwIcon } from "../../shared/icons";
import {
  deleteInformationTypeMutationOptions,
  informationTypeByIdQueryOptions,
  informationTypeHistoryQueryOptions,
  unarchiveInformationTypeMutationOptions,
} from "../../api/informationTypes";
import { prefetch } from "../../api/prefetch";
import type { InformationTypeById } from "../../api/informationTypes";
import type { DrawerHeaderProps } from "../../shared/Drawer/Drawer";
import { Drawer } from "../../shared/Drawer/Drawer";
import { usePermission } from "../../shared/permissions";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { InformationTypeEditDialog } from "./InformationTypeEditDialog";
import { InformationTypeArchiveConfirmDialog } from "./InformationTypeArchiveConfirmDialog";
import { ActionMenu, ActionMenuDeleteMenuItem, ActionMenuItemWithPermission } from "../../shared/ActionMenu";
import { ConfirmDeleteDialog } from "../../shared/ConfirmDeleteDialog";
import { useMutation } from "@tanstack/react-query";

type InformationTypeDrawerTab = "overview" | "history";

export const InformationTypeDrawerHeader = ({
  informationType,
  onClose,
  status,
  activeTab,
}: {
  status: DrawerHeaderProps["status"];
  informationType: InformationTypeById;
  onClose: () => void;
  activeTab: InformationTypeDrawerTab;
}) => {
  const { t } = useTranslation();
  const { paths } = usePrivacyNavigate();

  return (
    <Drawer.Header
      title={informationType.name}
      renderIcon={props => <InformationTypeIcon2 {...props} />}
      status={status}
      archivedAt={informationType.archivedAt}
      onClose={onClose}
      topRightSlot={
        <InformationTypeActionMenu
          informationTypeId={informationType.id}
          informationTypeName={informationType.name}
          archivedAt={informationType.archivedAt}
          onDeleted={() => onClose()}
          ariaLabelKey="GridDrawer.Menu.ButtonLabel"
        />
      }
      bottomSlot={
        <Stack sx={{ px: Drawer.PADDING_X }}>
          <Drawer.TabButtons>
            <Drawer.TabButton active={activeTab === "overview"} to={paths.informationTypesInline(informationType.id)}>
              {t("Dynamic.Drawer.OverviewButton")}
            </Drawer.TabButton>
            <Drawer.TabButton
              active={activeTab === "history"}
              to={paths.informationTypesHistory(informationType.id)}
              onHover={() => prefetch(informationTypeHistoryQueryOptions(informationType.id))}
            >
              {t("Dynamic.Drawer.HistoryButton")}
            </Drawer.TabButton>
          </Drawer.TabButtons>
        </Stack>
      }
    />
  );
};

type DialogState = "edit" | "archiveConfirm" | "unarchiveConfirm" | null;

export const InformationTypeActionMenu = ({
  informationTypeId,
  informationTypeName,
  archivedAt,
  onDeleted,
  ariaLabelKey,
  onHover,
}: {
  informationTypeId: string;
  informationTypeName: string;
  archivedAt?: string;
  onDeleted: () => void;
  ariaLabelKey?: TranslationKey;
  onHover?: () => void;
}) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canUpdate = hasPermission("nova-forms-api:information-type:update");

  const deleteMutation = useMutation(deleteInformationTypeMutationOptions());
  const unarchiveMutation = useMutation(unarchiveInformationTypeMutationOptions(informationTypeId));

  const [isOpen, setIsOpen] = useState(false);
  const [dialogState, setDialogState] = useState<DialogState>(null);

  const closeDialog = () => setDialogState(null);
  const openDialog = (state: Exclude<DialogState, null>) => {
    setDialogState(state);
    setIsOpen(false);
  };

  return (
    <>
      {dialogState === "edit" && (
        <InformationTypeEditDialog
          onClose={closeDialog}
          informationTypeId={informationTypeId}
          informationTypeName={informationTypeName}
        />
      )}

      {dialogState === "archiveConfirm" && (
        <InformationTypeArchiveConfirmDialog
          informationTypeId={informationTypeId}
          informationTypeName={informationTypeName}
          onClose={closeDialog}
        />
      )}

      {dialogState === "unarchiveConfirm" && (
        <ConfirmDeleteDialog
          isOpen
          onClose={closeDialog}
          onDelete={() => unarchiveMutation.mutateAsync()}
          headerText={t("InformationType.Archive.Unarchive.Title")}
          message={
            <Translate
              translationKey="InformationType.Archive.Unarchive.Message"
              values={{
                name: (
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    {informationTypeName}
                  </Box>
                ),
              }}
            />
          }
          confirmButtonLabel={t("InformationType.Archive.Unarchive.Button")}
          id="confirm-unarchive-information-type"
          onKeyDown={e => e.key === "Tab" && e.stopPropagation()}
        />
      )}

      <ActionMenu onTriggerHover={onHover} isOpen={isOpen} onOpenChange={setIsOpen} ariaLabelKey={ariaLabelKey}>
        {!archivedAt && (
          <ActionMenuItemWithPermission missingPermission={!canUpdate} onClick={() => openDialog("edit")}>
            <EditIcon />
            {t("InformationType.Edit.Label")}
          </ActionMenuItemWithPermission>
        )}

        {archivedAt ? (
          <ActionMenuItemWithPermission missingPermission={!canUpdate} onClick={() => openDialog("unarchiveConfirm")}>
            <RotateCcwIcon />
            {t("InformationType.Archive.RestoreLabel")}
          </ActionMenuItemWithPermission>
        ) : (
          <ActionMenuItemWithPermission
            missingPermission={!canUpdate}
            onMouseOver={() => prefetch(informationTypeByIdQueryOptions(informationTypeId))}
            onClick={() => openDialog("archiveConfirm")}
          >
            <ArchiveIcon />
            {t("InformationType.Archive.Label")}
          </ActionMenuItemWithPermission>
        )}

        <ActionMenuDeleteMenuItem
          missingPermission={!hasPermission("nova-forms-api:information-type:delete")}
          confirmDeleteDialog={{
            header: t("InformationType.Delete.ConfirmHeader"),
            message: (
              <>
                <Translate
                  translationKey="InformationType.Delete.ConfirmMessage"
                  values={{
                    name: (
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        {informationTypeName}
                      </Box>
                    ),
                  }}
                />
                {!archivedAt && ` ${t("InformationType.Delete.RecommendArchive")}`}
              </>
            ),
            delete: t("InformationType.Delete.ConfirmDelete"),
          }}
          onClose={() => setIsOpen(false)}
          onDelete={() => deleteMutation.mutateAsync(informationTypeId).then(() => onDeleted())}
        />
      </ActionMenu>
    </>
  );
};
